/**
 * One place describing every NotificationEvent the backend emits, so the
 * panel's tabs, status line and search stay in step with the enum
 * (Backend: src/types/enums/notificationEvent.ts).
 *
 * `kind` is the short status shown under each row; `tone` colours its dot.
 */
export const TYPE_META = {
  1: { kind: "Needs approval", tone: "action", groups: ["action", "records"] },
  2: { kind: "Needs approval", tone: "action", groups: ["action", "records"] },
  3: { kind: "Approved", tone: "good", groups: ["records"] },
  4: { kind: "Approved", tone: "good", groups: ["records"] },
  5: { kind: "Sent for signing", tone: "info", groups: ["contracts"] },
  6: { kind: "Rejected", tone: "bad", groups: ["records"] },
  7: { kind: "Rejected", tone: "bad", groups: ["records"] },
  10: { kind: "Needs approval", tone: "action", groups: ["action", "templates"] },
  11: { kind: "Approved", tone: "good", groups: ["templates"] },
  12: { kind: "Rejected", tone: "bad", groups: ["templates"] },
  13: { kind: "Needs approval", tone: "action", groups: ["action", "records"] },
  14: { kind: "Approved", tone: "good", groups: ["records"] },
  15: { kind: "Rejected", tone: "bad", groups: ["records"] },
};

export const GROUPS = [
  { key: "all", label: "All" },
  { key: "unread", label: "Unread" },
  { key: "action", label: "Action" },
  { key: "contracts", label: "Contracts" },
  { key: "templates", label: "Templates" },
  { key: "records", label: "Records" },
];

/**
 * Unread keys off `isSeen`, not `isRead`. Nothing in the backend ever writes
 * `isRead` — it is a dead column that stays false forever, so filtering on it
 * marked every notification unread permanently. `isSeen` is the flag
 * PATCH /notifications/mark-as-read actually sets.
 */
export const isUnread = (n) => !n?.isSeen;

export const matchesGroup = (n, key) => {
  if (key === "all") return true;
  if (key === "unread") return isUnread(n);
  return (TYPE_META[n?.eventId]?.groups || []).includes(key);
};

/** Glyph shown in the card's type chip, keyed by tone. */
export const TONE_GLYPH = {
  action: "!",
  good: "✓",
  bad: "×",
  info: "↗",
};

/** Text a row is searchable by: who, its status, and any resolved label. */
export const searchText = (n) => {
  const by = `${n?.createdBy?.firstName || ""} ${n?.createdBy?.lastName || ""}`;
  const kind = TYPE_META[n?.eventId]?.kind || "";
  return `${by} ${kind} ${n?.data?.label || ""}`.toLowerCase();
};

/**
 * Compact relative time. A notification list is scanned, not read, so "3h"
 * carries the same meaning as "3 hours ago" in a fraction of the width.
 */
export const shortWhen = (date) => {
  const then = new Date(date).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (s < 60) return "now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  const w = Math.round(d / 7);
  if (w < 5) return `${w}w`;
  const mo = Math.round(d / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.round(d / 365)}y`;
};
